/**
 * Mobile News Page Component
 * 
 * A mobile-optimized news display showing latest news with the same data sources
 * and image loading methods as the desktop NewsRoom and UnifiedNews components.
 * Now includes persistent image caching that survives navigation.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from './MobileLayout';
import apiHelper from '../../utils/apiHelper';
import imageCache from '../../utils/imageCache';
import { truncateText, formatDate, cleanJson, cleanAndDecodeUrls } from '../../utils/textUtils';
import './MobileNews.css';

const { FILE_NAMES } = require('../../utils/constants');

/**
 * Mobile News Component
 * 
 * @returns {JSX.Element} Mobile news page with article cards
 */
const MobileNews = () => {
  const navigate = useNavigate();
  const [weeklyNewsData, setWeeklyNewsData] = useState([]);
  const [newsState, setNewsState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayedCount, setDisplayedCount] = useState(10);
  const [loadingMore, setLoadingMore] = useState(false);

  // Get category color using same mapping as UnifiedNews
  const getCategoryColor = (category) => {
    if (!category) return 'ifia-green';
    
    const normalizedCategory = category.replace(/&/g, "").replace(/\s+/g, "");
    const colorMap = {
      'Intro': 'ifia-dark-green',
      'AdvocacyRegulatory': 'ifia-green',
      'Events': 'ifia-orange',
      'EngagementsCommunications': 'ifia-orange',
      'EngagementCommunications': 'ifia-purple',
      'CapabilityInnovation': 'ifia-dark-green',
      'WorkingGroupUpdates': 'ifia-blue',
      'Promoted': 'ifia-purple',
      'Featured': 'ifia-purple'
    };
    return colorMap[normalizedCategory] || 'ifia-green';
  };

  // Fetch news data using same method as NewsRoom
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const newsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MONTHLY_NEWS);
        setWeeklyNewsData(newsResult.fileContent);
      } catch (err) {
        setError('Error loading news: ' + err.message);
        console.error('Error fetching news:', err);
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  // Get random fallback image with tracking to avoid duplicates (same as UnifiedNews)
  const usedImages = new Set();
  const getRandomImage = (lastImageIndex) => {
    let randomIndex;
    let attempts = 0;
    
    do {
      randomIndex = Math.floor(Math.random() * 30) + 1;
      attempts++;
      
      // Clear the set if we've used most images to allow reuse
      if (usedImages.size >= 25) {
        usedImages.clear();
      }
      
      // Prevent infinite loops
      if (attempts > 100) {
        randomIndex = Math.floor(Math.random() * 30) + 1;
        break;
      }
      
    } while (usedImages.has(randomIndex) || Math.abs(randomIndex - lastImageIndex) < 5);

    usedImages.add(randomIndex);
    return `/assets/img/news/weekly/${randomIndex}.jpg`;
  };

  // Robust date parsing function (same as UnifiedNews)
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    
    try {
      // Handle ISO string format
      if (dateStr.includes('T')) {
        const parsed = new Date(dateStr);
        return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      }
      
      // Handle YYYY-MM-DD format
      if (dateStr.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
        const parsed = new Date(dateStr + 'T00:00:00.000Z');
        return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      }
      
      // Handle DD/MM/YYYY format
      if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        const parts = dateStr.split('/');
        if (parts[0] <= 31 && parts[1] <= 12) {
          const [day, month, year] = parts;
          const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
          if (!isNaN(parsed.getTime())) return parsed;
        }
        // Fallback to MM/DD/YYYY
        const [month, day, year] = parts;
        const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
        return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      }
      
      return new Date(dateStr);
    } catch (error) {
      return new Date(0);
    }
  };

  // Memoized processed and sorted news data (similar to UnifiedNews)
  const sortedNewsData = useMemo(() => {
    if (weeklyNewsData.length === 0) return [];
    
    // Filter out intro articles and sort by PortalPublishDate (newest first)
    const filteredNews = weeklyNewsData
      .filter(item => item.weeklyCategory !== 'Intro')
      .sort((a, b) => {
        const dateA = parseDate(a.portalPublishDate);
        const dateB = parseDate(b.portalPublishDate);
        return dateB.getTime() - dateA.getTime(); // Newest first
      });
    
    return filteredNews;
  }, [weeklyNewsData]);

  // Image loading function using persistent cache
  const loadImageForArticle = async (newsItem) => {
    const imageKey = `news-${newsItem.id}`;
    
    // Check if already cached (instant return)
    const cachedImage = imageCache.getCachedImage(imageKey);
    if (cachedImage) {
      return cachedImage;
    }

    // Define the loading function for this specific image
    const loadFunction = async () => {
      if (newsItem.image) {
        try {
          if (newsItem.image.includes('https://')) {
            return newsItem.image;
          } else {
            const authToken = await apiHelper.getAuthToken();
            const apiImageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${newsItem.image}`;
            
            const response = await fetch(apiImageUrl, {
              headers: { 'Authorization': `Bearer ${authToken}` }
            });
            
            if (response.ok) {
              const blob = await response.blob();
              return URL.createObjectURL(blob);
            } else {
              throw new Error('Failed to fetch image');
            }
          }
        } catch (err) {
          return getRandomImage();
        }
      } else {
        return getRandomImage();
      }
    };

    // Use the persistent cache
    return imageCache.getImage(imageKey, loadFunction);
  };

  // Initialize image loading state and load images when data changes
  useEffect(() => {
    if (sortedNewsData.length > 0) {
      // Initialize image loading state
      const initialState = sortedNewsData.map((newsItem) => {
        const imageKey = `news-${newsItem.id}`;
        const cachedImage = imageCache.getCachedImage(imageKey);
        
        return {
          id: newsItem.id,
          image: cachedImage,
          isLoading: !cachedImage && !imageCache.isLoading(imageKey)
        };
      });
      
      setNewsState(initialState);
      
      // Load missing images
      loadImages(sortedNewsData);
      setLoading(false);
    }
  }, [sortedNewsData]);

  // Image loading function with persistent cache
  const loadImages = async (newsArticles) => {
    try {
      // Load images that aren't cached
      const imagePromises = newsArticles.map(async (newsItem) => {
        const imageKey = `news-${newsItem.id}`;
        
        // Skip if already cached or loading
        if (imageCache.getCachedImage(imageKey) || imageCache.isLoading(imageKey)) {
          return {
            id: newsItem.id,
            image: imageCache.getCachedImage(imageKey),
            isLoading: imageCache.isLoading(imageKey)
          };
        }

        try {
          const imageUrl = await loadImageForArticle(newsItem);
          return {
            id: newsItem.id,
            image: imageUrl,
            isLoading: false
          };
        } catch (error) {
          console.error('Error loading image for article:', newsItem.id, error);
          return {
            id: newsItem.id,
            image: getRandomImage(),
            isLoading: false
          };
        }
      });

      const imageResults = await Promise.all(imagePromises);
      setNewsState(imageResults);
    } catch (error) {
      console.error('Error loading images:', error);
      // Fallback: set all items to use random images
      const fallbackState = newsArticles.map((newsItem) => ({
        id: newsItem.id,
        image: getRandomImage(),
        isLoading: false
      }));
      setNewsState(fallbackState);
    }
  };

  // Cleanup: Decrement reference counts when component unmounts
  useEffect(() => {
    return () => {
      // Decrement reference counts for all images this component was using
      newsState.forEach(item => {
        if (item.image) {
          const imageKey = `news-${item.id}`;
          imageCache.decrementRefCount(imageKey);
        }
      });
    };
  }, [newsState]);

  // Handle article click
  const handleArticleClick = (article) => {
    navigate(`/mobile/news/${article.id}`, {
      state: {
        currentNews: article,
        allNewsData: weeklyNewsData
      }
    });
  };

  if (loading) {
    return (
      <MobileLayout title="Latest News">
        <div className="mobile-news-loading">
          <div className="mobile-loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <p>Loading news...</p>
        </div>
      </MobileLayout>
    );
  }

  if (error) {
    return (
      <MobileLayout title="Latest News">
        <div className="mobile-news-error">
          <div className="mobile-error-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h3>Error Loading News</h3>
          <p>{error}</p>
          <button 
            className="mobile-retry-btn"
            onClick={() => window.location.reload()}
          >
            <i className="fas fa-refresh"></i>
            Try Again
          </button>
        </div>
      </MobileLayout>
    );
  }

  // Handle load more
  const handleLoadMore = async () => {
    setLoadingMore(true);
    
    // Simulate a small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Increase displayed count by 10
    setDisplayedCount(prev => Math.min(prev + 10, sortedNewsData.length));
    setLoadingMore(false);
  };

  const displayedNews = sortedNewsData.slice(0, displayedCount);
  const hasMoreNews = displayedCount < sortedNewsData.length;

  return (
    <MobileLayout title="Latest News">
      <div className="mobile-news">
        {/* Header */}
        <div className="mobile-news-header">
          <div className="mobile-news-count">
            Showing {displayedNews.length} of {sortedNewsData.length} article{sortedNewsData.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* News List */}
        <div className="mobile-news-list">
          {displayedNews.map((article) => {
            const articleState = newsState.find(state => state.id === article.id) || { isLoading: true, image: null };
            
            return (
              <div 
                key={article.id} 
                className="mobile-news-card"
                onClick={() => handleArticleClick(article)}
              >
                <div className="mobile-news-card-image">
                  {articleState.isLoading ? (
                    <div className="mobile-image-placeholder">
                      <i className="fas fa-spinner fa-spin"></i>
                    </div>
                  ) : (
                    <img 
                      src={articleState.image || getRandomImage()}
                      alt={article.title}
                      loading="lazy"
                    />
                  )}
                </div>
                
                <div className="mobile-news-card-content">
                  <h3 className="mobile-news-title">
                    {truncateText(cleanAndDecodeUrls(cleanJson(article.title)), 60)}
                  </h3>
                  
                  <p className="mobile-news-summary">
                    {truncateText(cleanAndDecodeUrls(cleanJson(article.summary || article.plainDescription || '')), 100)}
                  </p>
                  
                  <div className="mobile-news-meta">
                    <span className="mobile-news-date">
                      <i className="fas fa-calendar"></i>
                      {formatDate(article.portalPublishDate)}
                    </span>
                    {article.weeklyCategory && (
                      <span className={`mobile-news-category-tag ${getCategoryColor(article.weeklyCategory)}`}>
                        {article.weeklyCategory}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Load More Button */}
        {hasMoreNews && (
          <div className="mobile-load-more">
            <button 
              className="mobile-load-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Loading more...
                </>
              ) : (
                <>
                  <i className="fas fa-plus"></i>
                  Load More ({sortedNewsData.length - displayedCount} remaining)
                </>
              )}
            </button>
          </div>
        )}

        {sortedNewsData.length === 0 && !loading && (
          <div className="mobile-news-empty">
            <div className="mobile-empty-icon">
              <i className="fas fa-newspaper"></i>
            </div>
            <h3>No Articles Found</h3>
            <p>No news articles available at the moment.</p>
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

export default MobileNews; 